import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  OnDestroy,
  ViewChild,
  ElementRef} from '@angular/core';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { ViewToolsMode} from '../../../models/view-tools-mode.enum';
import { Select, Store } from '@ngxs/store';
import { EngineState } from 'src/app/engine/state/engine.state';
import { Observable, Subject } from 'rxjs';
import { ViewService } from '../../../view.service';
import { Fullscreen } from 'src/app/shared/libraries/fullscreen/fullscreen';
import { ViewState } from '../../../state/view.state';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { MediaModel } from 'src/app/story/models/media.model';
import { ViewActions } from '../../../state/view.actions';
import { ViewUIService } from '../../../view-ui.service';
import { ViewUI } from '../../../models/view-ui.enum';
import { takeUntil, take } from 'rxjs/operators';
import { MediaEnum } from 'src/app/story/models/media.enum';
import { Breakpoint } from 'src/app/breakpoint/models/breakpoint.enum';
import { BreakpointState } from 'src/app/breakpoint/state/breakpoint.state';
import { ViewChaptersComponent } from '../../view-chapters/view-chapters.component';
import { ViewMediaBarComponent } from '../view-media-bar/view-media-bar.component';
import { PopupService } from 'src/app/shared/features/popup/popup.service';
import { PopupRef } from 'src/app/shared/features/popup/popup.ref';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { ChapterReadyStateModel } from 'src/app/story/models/chapter-ready-state.model';
import { GoogleAnalyticsService } from 'ngx-google-analytics';

@Component({
  selector: 'app-view-tools',
  templateUrl: './view-tools.component.html',
  styleUrls: ['./view-tools.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    PopupService
  ]
})
export class ViewToolsComponent implements OnInit, OnDestroy {
  @ViewChild('chapterCount')
  chapterCount: ElementRef<HTMLDivElement>;

  @ViewChild('mediaToggle')
  mediaToggle: ElementRef<HTMLDivElement>;

  // ---------- Observables ----------
  @Select(StoryChaptersState.getSelectedChapterReadyState)
  selectedChapterReady$: Observable<ChapterReadyStateModel>;  
  
  @Select(EngineState.getActiveTool)
  activeTool$: Observable<ToolEnum>;

  @Select(ViewState.getPresentationActive)
  presentationActive$: Observable<boolean>;

  @Select(StoryChaptersState.getIsChapterAnimated)
  isChapterAnimated$: Observable<boolean>;

  @Select(EngineState.getShowLabels)
  showLabels$: Observable<boolean>;

  @Select(EngineState.getAnimationPlaying)
  animationPlaying$: Observable<boolean>;

  @Select(StoryLabelsState.getChapterLabels)
  labels$: Observable<LabelModel[]>;

  @Select(StoryChaptersState.getSelectedChapterTitle)
  chapterTitle$: Observable<string>;

  @Select(StoryChaptersState.getSelectedChapterIndex)
  chapterIndex$: Observable<number>;

  @Select(StoryChaptersState.getNextChapter)
  nextChapter$: Observable<ChapterModel>;

  @Select(StoryChaptersState.getPreviousChapter)
  previousChapter$: Observable<ChapterModel>;

  @Select(StoryChaptersState.getChapterCount)
  chapterCount$: Observable<number>;

  @Select(ViewState.getCanTouch)
  canTouch$: Observable<boolean>;

  // ---------- Class Variables ----------
  viewToolsMode = ViewToolsMode;
  viewUI = ViewUI;
  breakpointEnum = Breakpoint;
  canFullscreen = true;
  isFullscreen = false;
  breakpoint: Breakpoint;
  toolEnum = ToolEnum;
  mediaControlsEnum = MediaEnum;
  mediaTemplate: MediaEnum;
  chaptersPopup: PopupRef<ViewChaptersComponent>;
  mediaPopup: PopupRef<ViewMediaBarComponent>;
  fullscreen$: Observable<Event>;
  paginationVisible = false;
  mediaVisible = false;

  private ngUnsubscribe = new Subject();



  // ---------- Input / Output ----------
  @Input()
  animate = true;

  @Input()
  public get mode() {
    return this._mode;
  }
  public set mode(value) {
    this._mode = value;
  }
  private _mode = ViewToolsMode.Main;

  @Input()
  collapsePagination = false;

  @Input()
  collapseScrubber = false;

  @Output()
  modeChange = new EventEmitter<ViewToolsMode>();


  // ---------- Lifecycle Events ----------
  constructor(
    private viewService: ViewService,
    private viewUIService: ViewUIService,
    private store: Store,
    private changeDetectorRef: ChangeDetectorRef,
    private popupService: PopupService,
    private ga: GoogleAnalyticsService
  ) { }

  ngOnInit() {
    this.canFullscreen = Fullscreen.canFullscreen();
    this.initLayoutListener();
    this.initBreakpointListener();
    this.initChapterListener();
    this.initFullscreenListener();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initLayoutListener() {
    this.store.select(ViewState.getUI)
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((layout) => {
        this.mediaTemplate = layout.media.template;

        this.paginationVisible = this.viewUIService.isVisible(ViewUI.Pagination);
        this.mediaVisible = this.viewUIService.isVisible(ViewUI.Media);

        this.changeDetectorRef.markForCheck();
      });
  }

  initBreakpointListener() {
    this.store.select(BreakpointState.getBreakpoint)
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(breakpoint => {
        this.breakpoint = breakpoint;
        this.changeDetectorRef.markForCheck();
      });
  }

  initChapterListener() {
    this.store.select(StoryChaptersState.getSelectedChapterIndex)
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        if (this.chaptersPopup) {
          this.chaptersPopup.close();
        }
      });
  }

  initFullscreenListener() {
    this.fullscreen$ = Fullscreen.onFullscreenChange();
    this.fullscreen$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(event => {
        this.isFullscreen = Fullscreen.isFullscreen();
        this.changeDetectorRef.markForCheck();
      });
  }

  handleSelectTool(tool: ToolEnum) {
    // If user selects same tool twice, act as checkbox and deselect the tool
    const activeTool = this.store.selectSnapshot(EngineState.getActiveTool);
    const selectTool = (tool === activeTool) ? ToolEnum.Select : tool;

    this.viewService.selectTool(selectTool);
  }

  handlePlayClick() {
    this.viewService.toggleAnimation();
  }

  handleMenuClick() {
    this.store.dispatch(new ViewActions.ToggleMenu(true));
  }

  handlePreviousClick() {
    this.viewService.previousChapter('Toolbar');
  }

  handleNextClick() {
    this.viewService.nextChapter('Toolbar');
  }

  handleChapterCountClick() {
    this.toggleChaptersPopup();
  }

  handleMediaClick() {
    this.toggleMediaPopup();
  }

  toggleChaptersPopup() {
    if (this.chaptersPopup) {
      this.chaptersPopup.close();
    } else {
      this.chaptersPopup = this.popupService.open<ViewChaptersComponent>({
        origin: this.chapterCount.nativeElement,
        content: ViewChaptersComponent,
        width: '300px'
      });

      this.chaptersPopup.afterClosed$
        .pipe(
          takeUntil(this.ngUnsubscribe),
          take(1)
        )
        .subscribe((close) => {
          this.chaptersPopup = null;
        });
    }
  }

  toggleMediaPopup() {
    if (this.mediaPopup) {
      this.mediaPopup.close();
    } else {
      this.mediaPopup = this.popupService.open<ViewMediaBarComponent>({
        origin: this.mediaToggle.nativeElement,
        content: ViewMediaBarComponent,
        width: '300px'
      });

      this.mediaPopup.afterClosed$
        .pipe(
          takeUntil(this.ngUnsubscribe),
          take(1)
        )
        .subscribe((close) => {
          this.mediaPopup = null;
        });
    }
  }

  toggleToolbar(bar: 'media' | 'tools' | 'toolbar') {
    this.viewService.toggleToolbar(bar);
  }

  isEnabled(layout: ViewUI) {
    return this.viewUIService.isUIEnabled(layout);
  }

  isVisible(layout: ViewUI) {
    return this.viewUIService.isVisible(layout);
  }
}
